/**
 =========================================================
 * Material Dashboard 2 PRO React TS - v1.0.2
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
 * Copyright 2023 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import { useEffect, useState } from 'react';

// react-router components

// @material-ui core components
import { Theme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';

// Material Dashboard 2 PRO React TS examples components

// Custom styles for DashboardNavbar
import {
    navbar,
    navbarContainer,
    navbarDesktopMenu,
    navbarIconButton,
    navbarMobileMenu,
    navbarRow,
} from 'examples/Navbars/DashboardNavbar/styles';

// Material Dashboard 2 PRO React context
import {
    setMiniSidenav,
    setTransparentNavbar,
    useMaterialUIController,
} from 'context';

import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { FieldCell } from '@components/FieldCell';
import { GlobalSearch } from '@components/GlobalSearch';
import { NotificationsToggle } from '@components/NotificationsToggle';

// Declaring prop types for DashboardNavbar
interface Props {
    absolute?: boolean;
    light?: boolean;
    isMini?: boolean;
    title?: string;
    onTitleClick?: () => void;
}

const SKELETON_DARK = 'rgba(255,255,255,0.12)';
const SKELETON_LIGHT = 'rgba(0,0,0,0.11)';

function DashboardNavbar({ absolute, light, isMini, title, onTitleClick }: Props): JSX.Element {
    const { t } = useTranslation();
    const [navbarType, setNavbarType] = useState<
        'fixed' | 'absolute' | 'relative' | 'static' | 'sticky'
    >();
    const [controller, dispatch] = useMaterialUIController();

    const {
        miniSidenav,
        transparentNavbar,
        fixedNavbar,
        darkMode,
        userName,
    } = controller;

    const skeletonBg = darkMode ? SKELETON_DARK : SKELETON_LIGHT;

    const style = {
        title: {
            '&::first-letter': { textTransform: 'uppercase' },
        },
    };


    useEffect(() => {
        // Setting the navbar type
        if (fixedNavbar) {
            setNavbarType('sticky');
        } else {
            setNavbarType('static');
        }

        // A function that sets the transparent state of the navbar.
        function handleTransparentNavbar() {
            setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
        }

        /**
          The event listener that's calling the handleTransparentNavbar function when
          scrolling the window.
          */
        window.addEventListener('scroll', handleTransparentNavbar);

        // Call the handleTransparentNavbar function to set the state with the initial value.
        handleTransparentNavbar();

        // Remove event listener on cleanup
        return () => window.removeEventListener('scroll', handleTransparentNavbar);
    }, [dispatch, fixedNavbar]);



    const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);


    // Styles for the navbar icons
    const iconsStyle = ({ palette: { dark, white } }: Theme) => ({
        color: () => {
            let colorValue = light || darkMode ? white.main : dark.main;

            if (transparentNavbar && !light) {
                colorValue = darkMode ? white.main : dark.main;
            }

            return colorValue; // fsf
        },
    });

    useEffect(() => {
        // A recarga fica com o usePendencias: quem abre o sino vê o mesmo
        // número que o menu mostra, porque sai da mesma consulta.
    }, []);

    return (
        <AppBar
            position={absolute ? 'absolute' : navbarType}
            color="inherit"
            sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
            style={{ marginBottom: '1rem' }}
        >
            <Toolbar sx={(theme) => navbarContainer(theme, { darkMode })}>
                <MDBox
                    color="inherit"
                    mb={{ xs: 1, md: 0 }}
                    sx={(theme) => navbarRow(theme, { isMini, darkMode })}
                >
                    <Typography
                        component="h1"
                        sx={{
                            ...style.title,
                            cursor: onTitleClick ? 'pointer' : 'default',
                            '&:hover': onTitleClick
                                ? {
                                    textDecoration: 'underline',
                                }
                                : {},
                        }}
                        onClick={onTitleClick}
                    >
                        {title}
                    </Typography>
                    <IconButton
                        sx={(theme) => navbarDesktopMenu(theme, { darkMode })}
                        onClick={handleMiniSidenav}
                        aria-label={miniSidenav ? t('navbarScreen.abrirMenuLateral') : t('navbarScreen.fecharMenuLateral')}
                        size="small"
                        disableRipple
                    >
                        <Icon fontSize="medium" sx={iconsStyle}>
                            {miniSidenav ? 'menu_open' : 'menu'}
                        </Icon>
                    </IconButton>
                </MDBox>
                {isMini ? null : (
                    <MDBox sx={(theme) => navbarRow(theme, { isMini, darkMode })}>
                        <MDBox color={light ? 'white' : 'inherit'}>
                            <IconButton
                                size="small"
                                disableRipple
                                color="inherit"
                                sx={(theme) => navbarMobileMenu(theme, { darkMode })}
                                aria-label={miniSidenav ? t('navbarScreen.abrirMenuLateral') : t('navbarScreen.fecharMenuLateral')}
                                onClick={handleMiniSidenav}
                            >
                                <Icon
                                    sx={iconsStyle}
                                    fontSize="medium"
                                    title={
                                        miniSidenav
                                            ? t('navbarScreen.abrirMenuLateral')
                                            : t('navbarScreen.fecharMenuLateral')
                                    }
                                >
                                    {miniSidenav ? 'menu_open' : 'menu'}
                                </Icon>
                            </IconButton>

                            <MDBox
                                display="inline-flex"
                                alignItems="center"
                                sx={{ verticalAlign: 'middle', mr: 2, gap: 3 }}
                            >
                                <GlobalSearch />
                                {userName && (
                                    <FieldCell
                                        skeletonBg={skeletonBg}
                                        label={`${t('users.usuario')}: `}
                                        value={userName}
                                        isLoading={false}
                                        sx={{ flex: 1 }}
                                        inline
                                    />
                                )}
                            </MDBox>
                            <NotificationsToggle sx={(theme) => navbarIconButton(theme, { darkMode })} />
                        </MDBox>
                    </MDBox>
                )}
            </Toolbar>
        </AppBar>
    );
}

// Declaring default props for DashboardNavbar
DashboardNavbar.defaultProps = {
    absolute: false,
    light: false,
    isMini: false,
};

export default DashboardNavbar;
